import { useState, useEffect } from 'react';
import { Profile } from '../../types';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Loading } from '../../components/ui/Loading';
import { Empty } from '../../components/ui/Empty';
import { ErrorAlert } from '../../components/ui/ErrorAlert';
import { displayPhone } from '../../utils/phone';

export const EmployeeUserApprovals = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'USER')
        .eq('status', 'UNDER_REVIEW')
        .order('created_at', { ascending: false });
      if (dbError) throw dbError;
      setUsers((data ?? []) as Profile[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleStatus = async (id: string, status: 'APPROVED' | 'BLOCKED') => {
    const { error: dbError } = await supabase
      .from('profiles')
      .update({
        status,
        approved_by: status === 'APPROVED' ? user?.id : null,
        approved_at: status === 'APPROVED' ? new Date().toISOString() : null,
      })
      .eq('id', id);
    if (dbError) {
      setError(dbError.message);
      return;
    }
    await loadUsers();
  };

  if (loading) return <Loading full />;
  if (error) return <ErrorAlert message={error} onRetry={loadUsers} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">User Registration Requests</h1>
        <p className="text-sm text-gray-600">Review and approve new user registrations.</p>
      </div>
      {users.length === 0 ? (
        <Empty title="No pending registrations" message="There are no users waiting for approval." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Mobile</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Registered</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{u.full_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{u.phone ? displayPhone(u.phone) : '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{u.email ?? '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(u.created_at).toLocaleDateString('en-IN')}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleStatus(u.id, 'APPROVED')}
                        className="rounded bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleStatus(u.id, 'BLOCKED')}
                        className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
                      >
                        Block
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
